import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MdErrorOutline, MdLockOutline, MdMailOutline } from "react-icons/md";
import api from "../../services/axios";
import { MENSAGEM_MUITAS_TENTATIVAS } from "./loginError";

const inputCls =
  "w-full rounded-xl border border-gray-200 bg-white px-3 py-3 text-sm text-gray-800 transition-all focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-white/10 dark:bg-navy-900 dark:text-white";

interface NavState {
  email?: string;
  temporaryPassword?: string;
}

/** 400 traz `{ message }` (regras da senha) ou texto cru (credencial/sem marca de troca). */
export function mensagemDeErroDaTroca(error: unknown): string {
  const resp = (error as { response?: { status?: number; data?: unknown } } | null | undefined)?.response;
  const data = resp?.data;
  if (data && typeof data === "object") {
    const message = (data as { message?: unknown }).message;
    if (typeof message === "string" && message.trim()) return message;
  }
  if (resp?.status === 429) return MENSAGEM_MUITAS_TENTATIVAS;
  return "Não foi possível trocar a senha. Confira o e-mail e a senha temporária, ou peça um novo reset a quem administra sua conta.";
}

/**
 * Tela pública (sem token) da troca obrigatória após o reset feito pelo admin. E-mail e senha
 * temporária chegam pelo state da navegação — vindos do login — e nunca vão para storage.
 */
const ChangeTemporaryPassword: React.FC = () => {
  const navigate = useNavigate();
  const state = (useLocation().state as NavState | null) ?? {};
  const [email, setEmail] = useState(state.email ?? "");
  const [temporaryPassword, setTemporaryPassword] = useState(state.temporaryPassword ?? "");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError("A confirmação não é igual à nova senha.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/Auth/Change-Temporary-Password", {
        email,
        temporaryPassword,
        newPassword,
      });
      navigate("/auth/sign-in", { state: { message: data?.message } });
    } catch (err) {
      setError(mensagemDeErroDaTroca(err));
      setLoading(false);
    }
  };

  const campo = (
    id: string,
    label: string,
    value: string,
    onChange: (v: string) => void,
    opts: { type?: string; autoComplete: string; icon?: React.ReactNode }
  ) => (
    <div className="mt-4 flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-semibold text-gray-700 dark:text-gray-300">
        {label}
      </label>
      <div className="relative">
        {opts.icon}
        <input
          id={id}
          type={opts.type ?? "password"}
          autoComplete={opts.autoComplete}
          value={value}
          onChange={(ev) => onChange(ev.target.value)}
          className={`${inputCls} pl-9`}
          required
        />
      </div>
    </div>
  );
  const lock = (
    <MdLockOutline
      className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
      aria-hidden="true"
    />
  );

  return (
    <div className="flex min-h-dvh w-full items-center justify-center px-6 py-10">
      <main className="animate-fade-up w-full max-w-sm">
        <h1 className="text-2xl font-extrabold tracking-tight text-navy-800 dark:text-white">
          Troque sua senha
        </h1>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
          Você entrou com uma senha temporária. Defina uma nova senha para continuar.
        </p>

        <form onSubmit={submit} className="mt-6">
          {error && (
            <div
              role="alert"
              className="mb-2 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm font-medium text-red-700 dark:border-red-500/30 dark:bg-red-900/20 dark:text-red-300"
            >
              <MdErrorOutline className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{error}</span>
            </div>
          )}
          {campo("email", "E-mail", email, setEmail, {
            type: "email",
            autoComplete: "email",
            icon: (
              <MdMailOutline
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                aria-hidden="true"
              />
            ),
          })}
          {campo("temporaryPassword", "Senha temporária", temporaryPassword, setTemporaryPassword, {
            autoComplete: "current-password",
            icon: lock,
          })}
          {campo("newPassword", "Nova senha", newPassword, setNewPassword, {
            autoComplete: "new-password",
            icon: lock,
          })}
          {campo("confirmPassword", "Confirmar nova senha", confirmPassword, setConfirmPassword, {
            autoComplete: "new-password",
            icon: lock,
          })}

          <button
            type="submit"
            disabled={loading}
            className="mt-7 flex w-full items-center justify-center gap-2 rounded-xl bg-navy-800 px-5 py-3 text-sm font-bold text-white shadow-md transition-all hover:bg-navy-900 active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-navy-900 dark:hover:bg-gray-100"
          >
            {loading ? "Trocando…" : "Trocar senha"}
          </button>
          <button
            type="button"
            onClick={() => navigate("/auth/sign-in")}
            className="mt-3 w-full text-center text-sm font-semibold text-gray-600 hover:underline dark:text-gray-300"
          >
            Voltar ao login
          </button>
        </form>
      </main>
    </div>
  );
};

export default ChangeTemporaryPassword;
